// ~/apps/themusicchurch-app/frontend/src/pages/Store.jsx

import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { FiShoppingCart, FiHeart } from "react-icons/fi";
import Card from "../components/Common/Card";
import Button from "../components/Common/Button";

const StoreWrapper = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.colors.primary} 0%,
    ${(props) => props.theme.colors.secondary} 100%
  );
  color: white;
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: ${(props) => props.theme.fontSizes["5xl"]};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const ContentSection = styled.section`
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  max-width: 1400px;
  margin: 0 auto;
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing["3xl"]};
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.lg};
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const CategoryTab = styled.button`
  padding: ${(props) => props.theme.spacing.sm}
    ${(props) => props.theme.spacing.lg};
  background: ${(props) =>
    props.active ? props.theme.colors.primary : "transparent"};
  color: ${(props) => (props.active ? "white" : props.theme.colors.gray[700])};
  border: 2px solid
    ${(props) =>
      props.active ? props.theme.colors.primary : props.theme.colors.gray[300]};
  border-radius: 25px;
  cursor: pointer;
  transition: all ${(props) => props.theme.transitions.fast};
  font-weight: 500;

  &:hover {
    background: ${(props) => props.theme.colors.primary};
    color: white;
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const ProductGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${(props) => props.theme.spacing["2xl"]};
`;

const ProductCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${(props) => props.theme.shadows.md};
  transition: all ${(props) => props.theme.transitions.base};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${(props) => props.theme.shadows.xl};
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 280px;
  background: ${(props) => props.theme.colors.gray[200]};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => props.theme.fontSizes["3xl"]};
  color: ${(props) => props.theme.colors.gray[400]};
`;

const ProductBadge = styled.div`
  position: absolute;
  top: ${(props) => props.theme.spacing.md};
  right: ${(props) => props.theme.spacing.md};
  background: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.dark};
  padding: ${(props) => props.theme.spacing.xs}
    ${(props) => props.theme.spacing.sm};
  border-radius: 20px;
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-weight: 600;
`;

const WishlistButton = styled.button`
  position: absolute;
  bottom: ${(props) => props.theme.spacing.md};
  right: ${(props) => props.theme.spacing.md};
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${(props) => props.theme.shadows.md};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    transform: scale(1.1);
  }

  svg {
    color: ${(props) =>
      props.liked ? props.theme.colors.accent : props.theme.colors.gray[600]};
    fill: ${(props) => (props.liked ? props.theme.colors.accent : "none")};
  }
`;

const ProductInfo = styled.div`
  padding: ${(props) => props.theme.spacing.xl};
`;

const ProductTitle = styled.h3`
  color: ${(props) => props.theme.colors.primary};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const ProductPrice = styled.p`
  font-size: ${(props) => props.theme.fontSizes.xl};
  font-weight: 700;
  color: ${(props) => props.theme.colors.secondary};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const ProductDescription = styled.p`
  color: ${(props) => props.theme.colors.gray[600]};
  font-size: ${(props) => props.theme.fontSizes.sm};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const Store = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [likedProducts, setLikedProducts] = useState([]);

  const categories = ["all", "apparel", "books", "music", "accessories"];

  const products = [
    {
      id: 1,
      title: "Ministry Before Industry T-Shirt",
      price: "$24.99",
      category: "apparel",
      description: "Premium cotton t-shirt with our signature slogan",
      badge: "New",
    },
    {
      id: 2,
      title: "The Music Church Hoodie",
      price: "$44.99",
      category: "apparel",
      description: "Comfortable hoodie with embroidered logo",
    },
    {
      id: 3,
      title: "Worship Playlist USB",
      price: "$14.99",
      category: "music",
      description: "Curated collection of worship songs",
      badge: "Popular",
    },
    {
      id: 4,
      title: "Daily Devotional Book",
      price: "$19.99",
      category: "books",
      description: "365 days of spiritual guidance",
    },
    {
      id: 5,
      title: "Prayer Journal",
      price: "$12.99",
      category: "books",
      description: "Leather-bound journal for your prayers",
    },
    {
      id: 6,
      title: "Church Coffee Mug",
      price: "$9.99",
      category: "accessories",
      description: "Start your day with faith and coffee",
    },
    {
      id: 7,
      title: "Worship Music Album",
      price: "$9.99",
      category: "music",
      description: "Original worship songs by The Music Church",
    },
    {
      id: 8,
      title: "Faith Bracelet",
      price: "$7.99",
      category: "accessories",
      description: "Adjustable bracelet with scripture",
    },
  ];

  const filteredProducts =
    activeCategory === "all"
      ? products
      : products.filter((p) => p.category === activeCategory);

  const toggleLike = (productId) => {
    setLikedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Store - The Music Church</title>
        <meta
          name="description"
          content="Shop The Music Church merchandise, books, music, and more. All proceeds support our ministries."
        />
      </Helmet>

      <StoreWrapper>
        <HeroSection>
          <PageTitle>The Music Church Store</PageTitle>
          <p>Every purchase supports our ministries</p>
        </HeroSection>

        <ContentSection>
          <FilterBar>
            <CategoryTabs>
              {categories.map((category) => (
                <CategoryTab
                  key={category}
                  active={activeCategory === category}
                  onClick={() => setActiveCategory(category)}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </CategoryTab>
              ))}
            </CategoryTabs>

            <div style={{ color: "#6C757D" }}>
              {filteredProducts.length} products
            </div>
          </FilterBar>

          <ProductGrid
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} variants={itemVariants} layout>
                <ProductImage>
                  {product.badge && (
                    <ProductBadge>{product.badge}</ProductBadge>
                  )}
                  <span>Product Image</span>
                  <WishlistButton
                    onClick={() => toggleLike(product.id)}
                    liked={likedProducts.includes(product.id)}
                  >
                    <FiHeart />
                  </WishlistButton>
                </ProductImage>

                <ProductInfo>
                  <ProductTitle>{product.title}</ProductTitle>
                  <ProductPrice>{product.price}</ProductPrice>
                  <ProductDescription>{product.description}</ProductDescription>

                  <Button
                    variant="primary"
                    fullWidth
                    icon={<FiShoppingCart />}
                    onClick={() => console.log("Add to cart:", product.id)}
                  >
                    Add to Cart
                  </Button>
                </ProductInfo>
              </ProductCard>
            ))}
          </ProductGrid>
        </ContentSection>
      </StoreWrapper>
    </>
  );
};

export default Store;
